// Widget hỗ trợ của đội CNV trên hub SaaS — DÙNG CHUNG CNV Work + Site Connect (sếp Phú 13/10/2026:
// cụm «Help» của Site Connect «cũng cần làm share kernel để khách ở worksaas cũng dễ dàng sử dụng»).
// Gốc là lib/helpWidget.js của Site Connect (11/10/2026), nay chuyển về đây; Work bỏ «Trợ giúp AI».
//
// Widget là site livechat của đội hỗ trợ CNV ở Core (trợ lý AI + người trực + mục «Yêu cầu hỗ trợ»).
// Nhúng thẳng widget của Core = một hàng chờ duy nhất cho đội CG, không nuôi hệ hỗ trợ thứ hai.
//
// Máy chủ hub (`GET /help-widget`, kernel supportHelpWidget) trả: địa chỉ script, mã site, danh tính
// ĐÃ KÝ (người + claim workspace — khoá ký không bao giờ xuống trình duyệt) và ngữ cảnh. Core kiểm chữ
// ký rồi mới mở yêu cầu của CẢ workspace. Cụm chưa cấu hình ⇒ `enabled:false` ⇒ nút ẩn.
//
// Lối vào:
//   - Giao diện v2, màn ≥1024px: nút «Trợ giúp» trên thanh trên cùng (HelpMenu) ⇒ ẩn bong bóng mặc định
//     của widget (hai lối vào cho cùng một khung là rối mắt).
//   - v1 hoặc màn hẹp (thanh trên cùng bị ẩn): GIỮ bong bóng góc phải dưới — ẩn là cắt sạch lối vào.
//
// Đổi người / đổi workspace ⇒ `reset()` của widget: người kế tiếp trên cùng máy KHÔNG thấy yêu cầu của
// người trước. Đăng xuất ⇒ reset + giấu khung.

import api from '@host/axios';

const SCRIPT_ID = 'cnv-help-widget';
const HOST_ID = 'cnv-livechat';            // phần tử chủ do widget dựng (shadow DOM mode:'open')
const HIDE_STYLE_ID = 'cnv-help-hide-launcher';
const OFF_CLASS = 'cnv-help-off';
// Claim workspace sống 2 ngày (kernel livechat.WORKSPACE_CLAIM_MAX_AGE_SEC); tab hub để nguyên nhiều
// ngày thì xin claim mới khi người dùng quay lại tab mà gói đã cũ hơn mốc này.
const IDENTITY_REFRESH_MS = 6 * 3600 * 1000;
/**
 * Lớp của bảng thả xuống (chuông «Tin mới», chuông «Thông báo hỗ trợ») khi khung chat đang mở: widget đứng
 * ở z-index 2147483000 (kernel livechat-widget.js `.wrap`), khung cỡ lớn cao tới 84vh ⇒ bảng nào thấp hơn là
 * bị khung chat ĐÈ (thấy trên Site Connect 13/10/2026). Đứng trên `.wrap` nhưng DƯỚI lớp xem ảnh của widget
 * (`.lb` 2147483100).
 */
export const OVER_WIDGET_Z = 2147483050;

// Kho module treo globalThis (bundle nạp hai lần vẫn MỘT trạng thái — cùng lý do productNewsStore).
const G = (() => {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!g.__cnvHelpWidget) {
    g.__cnvHelpWidget = {
      cfg: null, loading: null, identityKey: null, inflight: null, fetchedAt: 0, screen: null,
      listeners: new Set(), wired: false,
    };
  }
  return g.__cnvHelpWidget;
})();

function lc() {
  return typeof window !== 'undefined' ? window.CnvLiveChat : undefined;
}

function notify() {
  for (const fn of G.listeners) {
    try { fn(getHelpState()); } catch { /* một người nghe hỏng không được kéo người khác */ }
  }
}

/** Trạng thái cho nút Trợ giúp: sẵn sàng chưa, link trung tâm trợ giúp, có «Chat», phạm vi workspace. */
export function getHelpState() {
  const cfg = G.cfg;
  return {
    ready: !!(cfg && cfg.enabled && typeof document !== 'undefined' && document.getElementById(HOST_ID)),
    helpUrl: cfg?.help_url || '',
    chat: !!cfg?.chat,                         // site hỗ trợ là loại chat (trợ lý AI + người trực)
    workspaceScope: !!cfg?.workspace_scope,    // yêu cầu chung của cả workspace (đã ký)
  };
}

export function subscribeHelp(fn) {
  G.listeners.add(fn);
  return () => G.listeners.delete(fn);
}

/**
 * Ẩn bong bóng mặc định CHỈ khi thanh trên cùng có nút Trợ giúp: v2 + màn ≥1024px (dưới mốc đó thanh
 * trên cùng bị ẩn — ui-v2.css). Chèn style vào shadow root của widget; chưa dựng xong ⇒ false để dò lại.
 */
function syncLauncherStyle() {
  try {
    const host = document.getElementById(HOST_ID);
    const root = host && host.shadowRoot;
    if (!root) return false;
    const v2 = document.documentElement.dataset.ui === 'v2';
    let style = root.getElementById(HIDE_STYLE_ID);
    if (!v2) { if (style) style.remove(); return true; }
    if (!style) {
      style = document.createElement('style');
      style.id = HIDE_STYLE_ID;
      style.textContent = '@media (min-width:1024px){.lwrap,.teaser{display:none!important}}';
      root.appendChild(style);
    }
    return true;
  } catch { return false; }
}

/** Widget dựng khung SAU khi gọi /session (một vòng mạng) — dò tới khi có, tối đa ~15 giây. */
function waitBooted(tries = 60) {
  return new Promise((resolve) => {
    const tick = (n) => {
      if (document.getElementById(HOST_ID)) { syncLauncherStyle(); notify(); return resolve(true); }
      if (n <= 0) return resolve(false);
      setTimeout(() => tick(n - 1), 250);
    };
    tick(tries);
  });
}

function wireOnce() {
  if (G.wired || typeof window === 'undefined') return;
  G.wired = true;
  if (typeof MutationObserver !== 'undefined') {
    // Công tắc v1/v2 đổi `data-ui` ngay trên trang ⇒ bật/tắt bong bóng theo.
    new MutationObserver(() => syncLauncherStyle())
      .observe(document.documentElement, { attributes: true, attributeFilter: ['data-ui'] });
  }
  // Quay lại tab sau nhiều giờ ⇒ xin claim workspace mới (widget dùng nó ở lượt /session kế tiếp).
  window.addEventListener('focus', () => {
    if (G.cfg && G.identityKey && Date.now() - G.fetchedAt > IDENTITY_REFRESH_MS) refreshHelpIdentity();
  });
}

/** Giấu / hiện toàn bộ khung (đăng xuất thì giấu — widget vẫn nằm đó chờ người kế tiếp). */
function setOff(off) {
  if (typeof document === 'undefined') return;
  let s = document.getElementById(`${HIDE_STYLE_ID}-doc`);
  if (!s) {
    s = document.createElement('style');
    s.id = `${HIDE_STYLE_ID}-doc`;
    s.textContent = `html.${OFF_CLASS} #${HOST_ID}{display:none!important}`;
    document.head.appendChild(s);
  }
  document.documentElement.classList.toggle(OFF_CLASS, !!off);
}

function applyIdentity(c) {
  const w = window.CnvLiveChat = window.CnvLiveChat || {};
  w.siteKey = c.site_key;
  w.user = c.user;
  w.attributes = { ...(c.context || {}), ...(G.screen ? { man_hinh: G.screen } : {}) };
}

async function fetchConfig() {
  try {
    const r = await api.get('/help-widget', { params: G.screen ? { screen: G.screen } : {} });
    return r?.data?.data || null;
  } catch { return null; }
}

/**
 * Gắn widget cho người + workspace hiện tại. Gọi lại mỗi khi người / workspace đổi; cùng khoá thì
 * không làm gì. Không có người (đăng xuất) ⇒ reset + giấu.
 */
export function syncHelpWidget({ userId, tenantId, screen } = {}) {
  if (typeof window === 'undefined') return Promise.resolve(false);
  wireOnce();
  if (screen) G.screen = String(screen).slice(0, 300);
  const key = userId && tenantId ? `${userId}:${tenantId}` : null;
  // Cùng khoá (StrictMode chạy effect hai lần, hoặc component dựng lại): chờ đúng lượt đang chạy.
  if (key === G.identityKey) return G.inflight || Promise.resolve(getHelpState().ready);
  G.identityKey = key;
  G.inflight = attach(key);
  return G.inflight;
}

async function attach(key) {
  if (!key) {
    G.cfg = null;
    const w = lc();
    if (w) {
      w.user = null;
      w.attributes = null;
      if (typeof w.reset === 'function' && document.getElementById(HOST_ID)) w.reset();
    }
    setOff(true);
    notify();
    return false;
  }

  const next = await fetchConfig();
  if (G.identityKey !== key) return false;            // người đã đổi tiếp trong lúc chờ mạng
  if (!next || !next.enabled || !next.script_url) {
    G.cfg = null; setOff(true); notify();
    return false;
  }

  const firstLoad = !G.cfg && !document.getElementById(SCRIPT_ID);
  G.cfg = next;
  G.fetchedAt = Date.now();
  applyIdentity(next);
  setOff(false);

  if (firstLoad) {
    G.loading = new Promise((resolve) => {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.async = true;
      s.src = next.script_url;
      s.onload = () => waitBooted().then(resolve);
      s.onerror = () => resolve(false);   // máy chủ hỗ trợ sập / bị chặn mạng ⇒ nút tự ẩn
      document.head.appendChild(s);
    });
    return G.loading;
  }

  // Script đã có: người / workspace khác ⇒ xoá phiên widget cũ, dựng lại với danh tính mới.
  await G.loading;
  const w = lc();
  if (w && typeof w.reset === 'function') w.reset();
  return waitBooted();
}

/**
 * Xin lại gói (claim workspace mới) cho CÙNG người + workspace — không reset widget: lượt /session kế
 * tiếp của widget (token 12 giờ hết hạn, đổi tab…) tự mang claim mới.
 */
export async function refreshHelpIdentity() {
  const key = G.identityKey;
  if (!key || !G.cfg) return false;
  const next = await fetchConfig();
  if (G.identityKey !== key || !next || !next.enabled) return false;
  G.cfg = next;
  G.fetchedAt = Date.now();
  applyIdentity(next);
  notify();
  return true;
}

/** Màn hình đang mở — đi kèm yêu cầu gửi lên, người xử lý khỏi phải hỏi «anh đang ở trang nào». */
export function setHelpScreen(screen) {
  G.screen = screen ? String(screen).slice(0, 300) : null;
  const w = lc();
  if (!w || !G.cfg) return;
  w.attributes = { ...(G.cfg.context || {}), ...(G.screen ? { man_hinh: G.screen } : {}) };
}

/** Thu khung hỗ trợ (menu Trợ giúp nằm TRONG thanh trên cùng — không nổi lên trên khung chat được). */
export function closeHelpPanel() {
  const w = lc();
  if (w && typeof w.close === 'function') w.close();
}

/** Mở khung chat (chỉ có nghĩa khi site hỗ trợ là loại chat — `getHelpState().chat`). */
export function openHelpChat() {
  const w = lc();
  if (w && typeof w.open === 'function') { w.open(); return true; }
  return false;
}

/** Mở thẳng form gửi yêu cầu mới. */
export function openHelpNew() {
  const w = lc();
  if (w && typeof w.openTicket === 'function') { w.openTicket(); return true; }
  return false;
}

/** Mở danh sách yêu cầu (của mình / của cả workspace). */
export function openHelpList() {
  const w = lc();
  if (w && typeof w.openTickets === 'function') { w.openTickets(); return true; }
  return false;
}
